import { useCallback, useEffect, useMemo, useState } from "react";
import { getDomainSubjects } from "../../services/organizationService";

// The subject catalog, loaded once per mount: `options` in the { value, label }
// shape MultiSelect / FieldSelect take, and `nameOf` for labelling a uuid. A
// failed load sets `failed` rather than passing for an empty catalog — an empty
// picker would read as "no subjects exist" — and `reload()` asks again.
// `seed` ([{uuid, name}], memoized) joins the catalog, so subjects already in
// hand stay named while it loads, after it failed, or once it drops them.
export default function useDomainSubjects({ enabled = true, seed } = {}) {
  const [subjects, setSubjects] = useState([]);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    setFailed(false);
    getDomainSubjects({ signal: controller.signal })
      .then((data) => setSubjects(Array.isArray(data) ? data : data?.results || []))
      .catch((err) => {
        if (err?.code !== "ERR_CANCELED") setFailed(true);
      });
    return () => controller.abort();
  }, [enabled, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return useMemo(() => {
    const names = new Map([...subjects, ...(seed || [])].map((s) => [s.uuid, s.name]));
    return {
      options: [...names].map(([value, label]) => ({ value, label })),
      nameOf: (uuid) => names.get(uuid) || "",
      failed,
      reload,
    };
  }, [subjects, seed, failed, reload]);
}
